import { MouseEvent, RefObject, useMemo, useState } from 'react';
import styled from 'styled-components';
import { v4 as uuid } from 'uuid';

import { getHumanReadableStepName } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenEditor/utils';
import PanZoomBox from 'dashboard/components-lib/PanZoomBox';
import {
  InquiryFunnelStepConditional,
  InquiryFunnelStepType,
} from 'dashboard/models/inquiry-funnel';
import { InquiryTemplate } from 'dashboard/models/inquiry-template';
import { MetricsTemplateFunnelFlow } from 'dashboard/models/metrics';
import { Z_INDICES } from 'dashboard/styles/variables';
import {
  WORKFLOW_STEP_CARD_HEIGHT,
  WORKFLOW_STEP_CARD_WIDTH,
} from 'dashboard/views/DashboardInquiryAnalyticsFunnelV2View/InquiryFunnelStepCard';
import InquiryFunnelTree, {
  FunnelNode,
} from 'dashboard/views/DashboardInquiryAnalyticsFunnelV2View/InquiryFunnelTree';
import { renderFunnelFlow } from 'dashboard/views/InquiryFunnelAnalyticsDetailedView/InquiryFunnelAnalyticsDetailedView';
import { Elevation } from 'design-system/variables/effect';
import useWindowDimensions from 'lib/hooks/useWindowDimensions';

const ZOOM_TO_FIT_PADDING = {
  top: 16,
  bottom: 16,
  left: 16,
  right: 16,
};

const MODAL_WIDTH = 650;
const MODAL_HEIGHT = 400;
const MODAL_OFFSET_MARGIN = 24;
const MAX_PATH_LENGTH = 10;

const calculateRatio = (currentStep: { value: number }, baseStep: { value: number }) => {
  const tentativeValue = currentStep.value / baseStep.value;
  if (isNaN(tentativeValue) || tentativeValue < 0) {
    return 0;
  }
  return Math.min(1, tentativeValue);
};

const calculateRatioRaw = (currentStep: { value: number }, baseStep: { value: number }) => {
  const tentativeValue = currentStep.value / baseStep.value;
  if (isNaN(tentativeValue)) {
    return Number.NaN;
  }
  return tentativeValue;
};

const convertToStepHelper = (
  templateFunnelFlow: MetricsTemplateFunnelFlow,
  baseMetrics: { value: number; percentage: number },
  parentNode: FunnelNode | undefined,
  dropOffMetrics?: { value: number; pathRatio: number; relativeRatio: number },
) => {
  const steps: FunnelNode[] = [];
  if (templateFunnelFlow.steps.length === 0) {
    return steps;
  }
  // we have two kinds of steps: Action steps and Conditional Steps.
  // A conditional step is the last branch before a branch.
  // otherwise it is an action step.

  // First step gets some special treatment
  const step = templateFunnelFlow.steps[0];
  // if we have no parent branch and no previous step, then we use ourselves as the base.
  // Otherwise, if we have a parent, we use the parent.
  // otherwise, we use the previous step
  const baseStepMetrics = parentNode == null ? step.metrics : parentNode.metrics;
  let parentCondition = null;

  if (templateFunnelFlow.condition != null) {
    parentCondition = {
      token: templateFunnelFlow.condition.token,
      name: templateFunnelFlow.condition.name,
      metrics: {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        ...parentNode.metrics,
      },
      pathRatio: calculateRatio(templateFunnelFlow.steps[0].metrics, baseMetrics),
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      relativeRatio: calculateRatio(templateFunnelFlow.steps[0].metrics, parentNode.metrics),
    };
  }
  steps.push({
    type: InquiryFunnelStepType.Action,
    token: step.id,
    name: step.name === step.id ? getHumanReadableStepName(step.id) : step.name,
    isTerminal: false,
    nodeToken: uuid(),
    metrics: {
      ...step.metrics,
    },
    pathRatio: calculateRatio(step.metrics, baseMetrics),
    relativeRatio: calculateRatio(step.metrics, baseStepMetrics),
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    parentCondition: parentCondition,
    dropOffMetrics: dropOffMetrics,
  });
  // end first step

  // The other steps.
  for (const step of templateFunnelFlow.steps.slice(1)) {
    // if we have no parent branch and no previous step, then we use ourselves as the base.
    // Otherwise, if we have a parent, we use the parent.
    // otherwise, we use the previous step
    const baseStepMetrics = steps[steps.length - 1].metrics;
    steps.push({
      type: InquiryFunnelStepType.Action,
      token: step.id,
      name: step.name === step.id ? getHumanReadableStepName(step.id) : step.name,
      isTerminal: false,
      nodeToken: uuid(),
      metrics: {
        ...step.metrics,
      },
      pathRatio: calculateRatio(step.metrics, baseMetrics),
      relativeRatio: calculateRatio(step.metrics, baseStepMetrics),
    });
  }
  // If it turns out that there are branches, we remove the last step (which is an action step) and replace
  // it with a conditional step instead.
  if (templateFunnelFlow.branches.length > 0) {
    const lastStep = steps.pop();
    const branches: FunnelNode[][] = [];
    const conditionals: InquiryFunnelStepConditional['conditionals'] = [];
    const conditionalStep = {
      type: InquiryFunnelStepType.Conditional,
      branches,
      conditionals,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      token: lastStep.token,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      name: lastStep.name,
      isTerminal: false,
      metrics: {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        ...lastStep.metrics,
      },
    };
    const conditionFunnelNode: FunnelNode = {
      ...conditionalStep,
      nodeToken: uuid(),
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      pathRatio: calculateRatio(lastStep.metrics, baseMetrics),
      relativeRatio: calculateRatio(
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        lastStep.metrics,
        parentNode == null
          ? steps.length > 0
            ? steps[steps.length - 1].metrics
            : /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              lastStep.metrics
          : steps.length > 0
            ? steps[steps.length - 1].metrics
            : parentNode.metrics,
      ),
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      parentCondition: lastStep.parentCondition,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      dropOffMetrics: lastStep.dropOffMetrics,
    };
    steps.push(conditionFunnelNode);
    let childrenTotal = 0;
    templateFunnelFlow.branches.forEach((branch) => {
      childrenTotal += branch.steps[0].metrics.value;
    });
    const lastTemplateFunnelStep = templateFunnelFlow.steps[templateFunnelFlow.steps.length - 1];
    // This number will be negative.
    const dropOffValue = childrenTotal - lastTemplateFunnelStep.metrics.value;
    const childrenDropOffMetrics = {
      value: dropOffValue,
      pathRatio: calculateRatioRaw({ value: dropOffValue }, baseMetrics),
      relativeRatio: calculateRatioRaw({ value: dropOffValue }, lastTemplateFunnelStep.metrics),
    };

    templateFunnelFlow.branches.forEach((branch, idx) => {
      const branchSteps = convertToStepHelper(
        branch,
        baseMetrics,
        conditionFunnelNode,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        idx === 0 ? childrenDropOffMetrics : null,
      );
      branches.push(branchSteps);
      conditionals.push({
        name: 'branch',
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        token: lastStep.token + '-' + idx.toString(),
      });
    });
  } else {
    if (steps.length > 0) {
      steps[steps.length - 1].isTerminal = true;
    }
  }
  return steps;
};

export const ConvertToFunnelNode = (
  templateFunnelFlow: MetricsTemplateFunnelFlow,
): FunnelNode[] => {
  if (templateFunnelFlow.steps.length === 0) {
    return [];
  }
  return convertToStepHelper(
    templateFunnelFlow,
    templateFunnelFlow.steps[0].metrics,
    undefined,
    undefined,
  );
};

const FunnelFlowContainer = styled.div<{ left: number; top: number; height?: number }>`
  position: fixed;
  left: ${({ left }) => left}px;
  top: ${({ top }) => top}px;
  width: ${MODAL_WIDTH}px;
  height: ${MODAL_HEIGHT}px;
  pointer-events: none;
  box-shadow: ${Elevation.Mid};
  z-index: ${Z_INDICES.modal};
`;

const InquiryFunnelAnalyticsFlowVisualizerContainer = styled.div`
  position: relative;
  display: flex;
  height: max(calc(100vh - 480px), 800px);
`;

export interface InquiryFunnelAnalyticsFlowVisualizerProps {
  selectedNodeToken?: string;
  withModal?: boolean;
  basePath?: string;
  templateFunnelFlow: MetricsTemplateFunnelFlow;
  template: InquiryTemplate;
  isAbsolute: boolean;
}

const findOptimalPositionForModal = (
  pathPosition: number[],
  containerPosition: DOMRect,
  windowDimensions: { height: number; width: number },
  selectedCardDimensions: number[],
): { top: number; left: number } => {
  // we try a few positions around the card and find the one that fits the most (i.e. one that minimizes the amount of
  // modal that is outside of the pan zoom area.
  const cardWidth =
    selectedCardDimensions.length === 0 ? WORKFLOW_STEP_CARD_WIDTH : selectedCardDimensions[0];
  const cardHeight =
    selectedCardDimensions.length === 0 ? WORKFLOW_STEP_CARD_HEIGHT : selectedCardDimensions[1];
  const leftX = pathPosition[0] - cardWidth / 2 - MODAL_WIDTH;
  const rightX = pathPosition[0] + cardWidth / 2 + MODAL_WIDTH;
  const topY = pathPosition[1] - cardHeight / 2 - MODAL_HEIGHT;
  const bottomY = pathPosition[1] + cardHeight / 2 + MODAL_HEIGHT;
  const candidates = [
    // top left
    {
      top: topY - MODAL_OFFSET_MARGIN,
      left: leftX - MODAL_OFFSET_MARGIN,
      right: leftX + MODAL_WIDTH - MODAL_OFFSET_MARGIN,
      bottom: topY + MODAL_HEIGHT - MODAL_OFFSET_MARGIN,
    },
    // bottom left
    {
      top: bottomY - MODAL_HEIGHT + MODAL_OFFSET_MARGIN,
      left: leftX - MODAL_OFFSET_MARGIN,
      right: leftX + MODAL_WIDTH - MODAL_OFFSET_MARGIN,
      bottom: bottomY + MODAL_OFFSET_MARGIN,
    },
    // bottom right
    {
      top: bottomY - MODAL_HEIGHT + MODAL_OFFSET_MARGIN,
      left: rightX - MODAL_WIDTH + MODAL_OFFSET_MARGIN,
      right: rightX + MODAL_OFFSET_MARGIN,
      bottom: bottomY + MODAL_OFFSET_MARGIN,
    },
    // to the right
    {
      top: pathPosition[1] - MODAL_HEIGHT / 2,
      left: rightX - MODAL_WIDTH + MODAL_OFFSET_MARGIN,
      right: rightX + MODAL_OFFSET_MARGIN,
      bottom: pathPosition[1] + MODAL_HEIGHT / 2,
    },
    // top right
    {
      top: topY - MODAL_OFFSET_MARGIN,
      left: rightX - MODAL_WIDTH + MODAL_OFFSET_MARGIN,
      right: rightX + MODAL_OFFSET_MARGIN,
      bottom: topY + MODAL_HEIGHT - MODAL_OFFSET_MARGIN,
    },
    // top
    {
      top: topY - MODAL_OFFSET_MARGIN,
      left: pathPosition[0] - MODAL_WIDTH / 2,
      right: pathPosition[0] + MODAL_WIDTH / 2,
      bottom: topY + MODAL_HEIGHT - MODAL_OFFSET_MARGIN,
    },
    // bottom
    {
      top: bottomY + MODAL_OFFSET_MARGIN - MODAL_HEIGHT,
      left: pathPosition[0] - MODAL_WIDTH / 2,
      right: pathPosition[0] + MODAL_WIDTH / 2,
      bottom: bottomY + MODAL_OFFSET_MARGIN,
    },
  ];
  const scores = candidates.map((coords) => {
    const leftScore = Math.min(0, coords.left - Math.max(containerPosition.left, coords.left));
    const rightScore = Math.min(
      0,
      Math.min(containerPosition.right, coords.right, windowDimensions.width) - coords.right,
    );
    const topScore = Math.min(0, coords.top - Math.max(containerPosition.top, coords.top));
    const bottomScore = Math.min(
      0,
      Math.min(containerPosition.bottom, coords.bottom, windowDimensions.height) - coords.bottom,
    );
    return leftScore + rightScore + topScore + bottomScore;
  });
  let highestScoreIndex = 0;
  for (let i = 1; i < scores.length; i++) {
    if (scores[i] > scores[highestScoreIndex]) {
      highestScoreIndex = i;
    }
  }
  return { left: candidates[highestScoreIndex].left, top: candidates[highestScoreIndex].top };
};

const InquiryFunnelAnalyticsFlowVisualizer = ({
  selectedNodeToken,
  templateFunnelFlow,
  template,
  isAbsolute,
}: InquiryFunnelAnalyticsFlowVisualizerProps) => {
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const [activeNodeToken, setActiveNodeToken] = useState<string>(selectedNodeToken);
  const [selectedPath, setSelectedPath] = useState<FunnelNode[]>([]);
  const [pathPosition, setPathPosition] = useState<number[]>([]);
  const [containerPosition, setContainerPosition] = useState<DOMRect>();
  const [selectedCardDimensions, setSelectedCardDimensions] = useState([]);
  const windowDimensions = useWindowDimensions();

  const selectNode = (selectedNodeToken: string) => {
    setActiveNodeToken(selectedNodeToken);
  };

  const steps = useMemo(() => {
    return ConvertToFunnelNode(templateFunnelFlow);
  }, [templateFunnelFlow]);
  const modalPosition =
    pathPosition.length === 0
      ? null
      : findOptimalPositionForModal(
          pathPosition,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          containerPosition,
          windowDimensions,
          selectedCardDimensions,
        );

  return (
    <>
      <InquiryFunnelAnalyticsFlowVisualizerContainer key={templateFunnelFlow.token}>
        <PanZoomBox
          zoomToFit={true}
          zoomToFitPadding={ZOOM_TO_FIT_PADDING}
          scrollBoxUsePrimaryThemeColor={true}
          scrollBoxBackgroundType='dotted'
          preventPageScrollOnPan={true}
        >
          <InquiryFunnelTree
            activeNodeToken={activeNodeToken}
            selectNode={selectNode}
            steps={steps}
            template={template}
            selectedPath={selectedPath}
            onSelectPath={(
              event: MouseEvent,
              scrollRef: RefObject<HTMLDivElement>,
              selectedPath: FunnelNode[],
            ) => {
              setSelectedPath(selectedPath);
              if (selectedPath.length === 0) {
                setPathPosition([]);
              } else {
                setPathPosition([
                  (event.currentTarget.getBoundingClientRect().left +
                    event.currentTarget.getBoundingClientRect().right) /
                    2,
                  (event.currentTarget.getBoundingClientRect().top +
                    event.currentTarget.getBoundingClientRect().bottom) /
                    2,
                ]);
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                setContainerPosition(scrollRef.current.getBoundingClientRect());
                setSelectedCardDimensions([
                  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                  event.currentTarget.getBoundingClientRect().width,
                  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                  event.currentTarget.getBoundingClientRect().height,
                ]);
              }
            }}
            isAbsolute={isAbsolute}
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            inquiryTemplateVersionId={templateFunnelFlow.token}
          />
        </PanZoomBox>
      </InquiryFunnelAnalyticsFlowVisualizerContainer>
      {pathPosition.length === 0 ? null : (
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        <FunnelFlowContainer left={modalPosition.left} top={modalPosition.top}>
          {renderFunnelFlow(
            templateFunnelFlow,
            selectedPath.slice(0, MAX_PATH_LENGTH).map((step) => step.token),
            isAbsolute,
            {
              height: MODAL_HEIGHT,
            },
          )}
        </FunnelFlowContainer>
      )}
    </>
  );
};

export default InquiryFunnelAnalyticsFlowVisualizer;
